<template>
    <div class="login-page">
        <section class="login-side" aria-hidden="true">
            <img src="../assets/logo.svg" alt="" class="side-logo" />
            <div class="side-copy">
                <h2>Run the accounts behind AD168</h2>
                <p>Approve plans, look after customers and pay affiliates from one place.</p>
            </div>
            <span class="side-foot">Ops console</span>
        </section>

        <section class="login-main">
            <div class="login-card">
                <img src="../assets/mark.svg" alt="AD168" class="login-mark" />
                <h1 class="login-title">Sign in</h1>
                <p class="login-sub">Authorized personnel only.</p>

                <a-form :model="form" layout="vertical" @submit.prevent="handleLogin">
                    <a-form-item label="Email" name="username" :help="formErrors.username" :validate-status="formErrors.username ? 'error' : ''">
                        <a-input v-model:value="form.username" size="large" placeholder="ops_admin@ad168.org" autocomplete="username">
                            <template #prefix>
                                <UserOutlined class="text-gray" />
                            </template>
                        </a-input>
                    </a-form-item>

                    <a-form-item label="Password" name="password" :help="formErrors.password" :validate-status="formErrors.password ? 'error' : ''">
                        <a-input-password v-model:value="form.password" size="large" placeholder="Password" visibilityToggle autocomplete="current-password">
                            <template #prefix>
                                <LockOutlined class="text-gray" />
                            </template>
                        </a-input-password>
                    </a-form-item>

                    <a-button type="primary" html-type="submit" size="large" block :loading="loading" class="login-button">
                        Sign in
                    </a-button>
                </a-form>
            </div>
        </section>
    </div>
</template>

<script>
import { UserOutlined, LockOutlined } from '@ant-design/icons-vue';
import { auth } from '@/firebase/config';
import { signInWithEmailAndPassword } from 'firebase/auth';
import { message } from 'ant-design-vue';

export default {
    components: {
        UserOutlined,
        LockOutlined
    },
    data() {
        return {
            form: {
                username: '', 
                password: ''
            },
            formErrors: {
                username: '',
                password: ''
            },
            loading: false,
            allowedEmail: 'ops_admin@ad168.org'
        };
    },
    methods: {
        validateForm() {
            let isValid = true;
            this.formErrors = {
                username: '',
                password: ''
            };

            if (!this.form.username) {
                this.formErrors.username = 'Please enter your email';
                isValid = false;
            } else if (this.form.username !== this.allowedEmail) {
                this.formErrors.username = 'Access restricted to authorized personnel only';
                isValid = false;
            }

            if (!this.form.password) {
                this.formErrors.password = 'Please enter your password';
                isValid = false;
            }

            return isValid;
        },
        async handleLogin() {
            if (!this.validateForm()) {
                return;
            }

            this.loading = true;

            try {
                const userCredential = await signInWithEmailAndPassword(
                    auth,
                    this.form.username,
                    this.form.password
                );

                if (userCredential.user.email !== this.allowedEmail) {
                    await auth.signOut();
                    message.error('Access restricted to authorized personnel only');
                    return;
                }

                message.success('Login successful');
                this.$router.push('/dashboard');
            } catch (error) {
                let errorMessage = 'Failed to login';
                
                switch (error.code) {
                    case 'auth/invalid-email':
                        errorMessage = 'Invalid email format';
                        break;
                    case 'auth/user-not-found':
                    case 'auth/wrong-password':
                        errorMessage = 'Invalid email or password';
                        break;
                    case 'auth/too-many-requests':
                        errorMessage = 'Too many failed attempts. Please try again later';
                        break;
                }
                
                message.error(errorMessage);
            } finally {
                this.loading = false;
            }
        }
    }
};
</script>

<style>
.login-page { display: grid; grid-template-columns: minmax(0, 5fr) minmax(0, 6fr); min-height: 100vh; background: var(--bg); }

.login-side {
    position: relative; overflow: hidden; display: flex; flex-direction: column; justify-content: space-between;
    padding: 40px 48px; color: #fff; background: #1a1411;
}
.login-side::before {
    content: ''; position: absolute; width: 520px; height: 520px; right: -180px; top: -160px; border-radius: 50%;
    background: radial-gradient(circle, rgba(219, 14, 0, 0.55), rgba(219, 14, 0, 0) 68%);
}
.login-side::after {
    content: ''; position: absolute; width: 420px; height: 420px; left: -160px; bottom: -180px; border-radius: 50%;
    background: radial-gradient(circle, rgba(255, 184, 62, 0.32), rgba(255, 184, 62, 0) 68%);
}
.side-logo { position: relative; z-index: 1; width: 150px; }
.side-copy { position: relative; z-index: 1; max-width: 380px; }
.side-copy h2 { margin: 0 0 12px; color: #fff; font-size: 34px; line-height: 1.12; letter-spacing: -0.03em; font-weight: 600; }
.side-copy p { margin: 0; color: rgba(255, 255, 255, 0.7); font-size: 16px; line-height: 1.5; }
.side-foot { position: relative; z-index: 1; color: rgba(255, 255, 255, 0.5); font-size: 13px; }

.login-main { display: flex; align-items: center; justify-content: center; padding: 32px; }
.login-card { width: 100%; max-width: 400px; }
.login-mark { width: 44px; height: 44px; margin-bottom: 24px; }
.login-title { margin: 0; font-size: 28px; font-weight: 600; letter-spacing: -0.02em; }
.login-sub { margin: 6px 0 28px; color: var(--ink-2); }
.login-button { margin-top: 8px; font-weight: 500; }
.text-gray { color: var(--ink-3); }

@media (max-width: 860px) {
    .login-page { grid-template-columns: minmax(0, 1fr); }
    .login-side { display: none; }
}
</style>
